import type { BacklogTaskResult, UpdateBacklogIssuePayload } from '@ai-meeting/shared'

/**
 * The ticket as it stands, flattened out of whichever reply carried it.
 *
 * `POST /tasks` reports all of this for a matched row, and so does the reply
 * to a refused `PATCH`. Taking the same shape from both is what lets the panel
 * re-render on a refusal without a second read — and it is why the panel opens
 * with no round trip at all.
 */
export interface ConfirmTicket {
  issueKey: string
  url: string | null
  summary: string
  priority: string | null
  assigneeId: number | null
  assigneeName: string | null
  dueDate: string | null
  /**
   * Three values, three different things. A string is the description, `''`
   * means the ticket has none, and `null` means it could not be read — the
   * last of which must not be shown as an empty ticket.
   */
  description: string | null
  /** Echoed back as `confirmed_updated`; the server refuses if it has moved. */
  updated: string | null
  /** false → these came from a cached snapshot, so say so before overwriting. */
  isFresh: boolean
}

/**
 * Whether this row would overwrite a ticket that still exists unchanged.
 *
 * Every such row is held out of the batch and sent through the confirmation
 * panel instead, which is not how `POST /tasks` behaves on its own: a
 * confident match comes back `updated`, meaning the batch overwrote a ticket
 * without anybody being shown what was on it. Backlog has no delete API, so
 * that is not something to find out afterwards.
 *
 * A low-confidence match already stops at `matched`. This makes the two
 * behave the same, so "Update" on this table always means "nothing has been
 * written yet".
 */
export function needsConfirmation(row: BacklogTaskResult | undefined): boolean {
  if (!row) return false
  // Already settled, one way or the other.
  if (row.status === 'created' || row.status === 'updated' || row.status === 'failed') return false
  if (row.action_type !== 'update') return false
  return Boolean(row.matched_issue_key ?? row.issue_key)
}

/**
 * Whether the server settled this row by finding the issue it would have made.
 *
 * `POST /tasks` stamps every issue it creates with a marker built from the
 * meeting id and the title, and looks that marker up before creating anything,
 * so a second press comes back `skipped_duplicate` instead of a second ticket.
 * The row therefore carries a ticket key and has nothing left to do — which is
 * neither "about to be created" nor "still waiting", the two things the table
 * used to say about it.
 *
 * @param row The verdict for this action item.
 */
export function isAlreadyCreated(row: BacklogTaskResult | undefined): boolean {
  return row?.status === 'skipped_duplicate'
}

/**
 * Roughly how many lines a description will take in the panel's left column.
/**
 * Roughly how many lines a description will take in the panel's left column.
 *
 * Counted rather than measured, because measuring means laying the text out
 * and reading `scrollHeight`, which is zero in a test — the expander would be
 * impossible to cover. Counted by *line* rather than by total length, because
 * a newline costs a whole line however short it is: the usual first line here
 * is `Nội dung công việc:`, nineteen characters that fill one.
 *
 * `CHARS_PER_LINE` was taken from a browser: at this column's measured width
 * and `text-sm`, the sample from the design wrapped to exactly the four lines
 * this returns for it.
 *
 * @param text The description as the ticket has it.
 * @returns Lines, never fewer than one.
 */
export function estimateDescriptionLines(text: string): number {
  const CHARS_PER_LINE = 50
  return text
    .split('\n')
    .reduce((lines, line) => lines + Math.max(1, Math.ceil(line.length / CHARS_PER_LINE)), 0)
}

/** Read a matched row's view of the ticket it would overwrite. */
export function ticketFromRow(row: BacklogTaskResult): ConfirmTicket | null {
  const issueKey = row.matched_issue_key ?? row.issue_key
  if (!issueKey) return null
  return {
    issueKey,
    url: row.url,
    summary: row.current_summary ?? row.title,
    priority: row.current_priority,
    assigneeId: row.current_assignee_id,
    assigneeName: row.current_assignee_name,
    dueDate: row.current_deadline,
    // Normalised, because a missing field and an explicit null mean the same
    // thing here — the description could not be read — and the two have to
    // behave the same. An older AI service sends no field at all, and the
    // panel branches on the value rather than on its presence.
    description: row.current_description ?? null,
    updated: row.current_updated,
    // Only an explicit false means stale. Missing is not a claim either way,
    // and warning on every row an older service answered is noise.
    isFresh: row.current_is_fresh !== false,
  }
}

/**
 * The fields a reviewer can change before agreeing to overwrite a ticket.
 */
export interface ConfirmDraft {
  summary: string
  priority: string
  assigneeId: number | null
  dueDate: string
  description: string
  /** True once the description box has been edited, which is what sends it. */
  descriptionTouched: boolean
}

/**
 * What to offer when the project's own priority names could not be read.
 *
 * Backlog ships these four and most projects keep them, but a project is free
 * to rename them — which is why this is the fallback and `project-meta` is the
 * source. Writing a name the project does not define is refused with 422.
 */
export const FALLBACK_PRIORITIES = ['High', 'Normal', 'Low'] as const

/**
 * The draft as the panel opens: the action item's own wording, against the
 * ticket's current values for everything the minutes do not carry.
 *
 * Priority seeds from the ticket rather than from a default, so one somebody
 * deliberately raised to High is not quietly dropped back to Normal by a
 * reviewer who never looked at that row.
 *
 * The description seeds from the ticket too, not from the action item: this
 * box is the ticket's description, and pre-filling it with the minutes would
 * propose replacing the whole body with a one-line action item nobody asked to
 * write. An unreadable description (`null`) starts empty and stays unsent
 * until somebody types.
 *
 * @param ticket The ticket as the row reported it.
 * @param proposed What the sync would have written: the action item's content,
 *   the member resolved for it, and its deadline.
 */
export function initialDraft(
  ticket: ConfirmTicket,
  proposed: { summary: string; assigneeId: number | null; dueDate: string }
): ConfirmDraft {
  return {
    summary: proposed.summary.trim() || ticket.summary,
    priority: ticket.priority ?? 'Normal',
    assigneeId: proposed.assigneeId ?? ticket.assigneeId,
    dueDate: proposed.dueDate || (ticket.dueDate ?? ''),
    description: ticket.description ?? '',
    descriptionTouched: false,
  }
}

/**
 * Only what the reviewer actually changed.
 *
 * Built by comparing against the ticket rather than by sending the whole
 * draft: every field present is a field the AI service writes, and writing a
 * value back over the identical value still counts as an edit in Backlog's
 * own history.
 *
 * @param draft What is on screen when Confirm is pressed.
 * @param ticket The ticket as the panel read it, including the `updated` stamp
 *   that makes the write refusable if it has moved on since.
 */
export function buildUpdatePayload(
  draft: ConfirmDraft,
  ticket: ConfirmTicket
): UpdateBacklogIssuePayload {
  const payload: UpdateBacklogIssuePayload = { confirmed_updated: ticket.updated }
  if (draft.summary.trim() && draft.summary.trim() !== ticket.summary) {
    payload.summary = draft.summary.trim()
  }
  if (draft.priority && draft.priority !== ticket.priority) payload.priority = draft.priority
  if (draft.assigneeId !== ticket.assigneeId) payload.assignee_id = draft.assigneeId
  if (draft.dueDate !== (ticket.dueDate ?? '')) payload.due_date = draft.dueDate || null
  if (draft.descriptionTouched) {
    payload.description = draft.description
    // Still sent although the route now treats an absent field as untouched:
    // the flag is still in the upstream signature, and if it is the thing that
    // gates the write then dropping it loses descriptions silently.
    payload.write_description = true
  }
  return payload
}
